import {
  isPlayerForm,
  type FixtureSeasonRecord,
  type PublicFixture,
} from "../../services/api/schemas";

/**
 * The second line of a fixture-list row: where it is played, and how the two
 * clubs have been going. Pure, so what a row is allowed to claim is decided in
 * one testable place rather than inside JSX.
 *
 * ── WHAT THE ANONYMOUS ROW ACTUALLY CARRIES, PER SPORT (measured 2026-09-08
 * against a saved copy of the published `fixtures-public` file, 68 rows) ─────
 *
 *   NFL (16 rows)  `venue` on 16/16 — "Lumen Field", "Melbourne Cricket
 *                  Ground". `lastFive` is the per-club OBJECT: each side's LAST
 *                  COMPLETED SEASON record, plus points for/against per game
 *                  and home/away splits.
 *
 *   FOOTBALL (52)  NO `venue` KEY AT ALL — not null, absent. The exporter
 *                  writes `"venue": None` for every fixture (see
 *                  `soccer/export_fixture_pages.py`: "build_artifacts.py's
 *                  build_fixtures has no venue field — honestly None rather
 *                  than guessed") and `derive_public_fixture` omits a falsy
 *                  one. `lastFive` is the ARRAY of previous MEETINGS between
 *                  these two clubs. There is no club-form field anywhere in
 *                  the football payload, at any tier.
 *
 * ⚠️ SO THE ROW SHOWS FORM ON THE NFL AND NOTHING ON FOOTBALL, AND THAT IS THE
 * HONEST ANSWER RATHER THAN AN OVERSIGHT. Two things were deliberately NOT
 * done to make football look equal:
 *
 *   · Football's `lastFive` is NOT rendered here. It is the head-to-head
 *     history, not either club's form, and the two are different facts — see
 *     `fixtureLastFiveSchema`'s own "DO NOT COERCE ONE INTO THE OTHER". Worse
 *     for a LIST specifically: a row can already carry a "Kicked off" badge,
 *     and a scoreline printed beside that badge reads as THIS match's score.
 *     The meeting history stays on the fixture page, under a heading that says
 *     what it is.
 *
 *   · Nothing is parsed out of a meeting's `text` ("Cagliari 0-2 Lecce") to
 *     synthesise a W/D/L run. Real club form needs the club's last five games
 *     against ANYONE, which no tier of this payload contains. Deriving five
 *     letters from five head-to-heads would produce a plausible-looking string
 *     that is not the thing it is labelled as.
 *
 * Football venue and football club form both need an EXPORTER change to exist
 * at all; see this module's note in the handover rather than adding a
 * client-side stand-in.
 *
 * ── TIER ─────────────────────────────────────────────────────────────────────
 * Everything here is already published on the anonymous row and already passes
 * `_assert_public_is_clean` server-side: `venue` and `lastFive` are both in the
 * exporter's `PUBLIC_FIXTURE_KEYS`, and the no-digits rule is a rule about
 * `read` (Kev's opinion), not about factual history — the NFL exporter says so
 * in as many words where it builds these records. A W-L record and a stadium
 * name are facts about the fixture, not Kev's call on it, so no probability,
 * fair price or gap crosses the tier line by being here.
 */

/**
 * The headline record, in the order the exporter's own fields win: its
 * formatted `record` string first, then one derived from the raw counts, then
 * nothing. Never a partial line like "14-" — either the record is known or it
 * is not.
 *
 * Shared with `TeamNewsSection`, which renders the same record on the fixture
 * page: one implementation, so a club cannot read "4-13" in the list and
 * something else a tap later.
 */
export function seasonRecordLine(record: FixtureSeasonRecord): string | null {
  if (typeof record.record === "string" && record.record.length > 0) return record.record;
  if (typeof record.wins !== "number" || typeof record.losses !== "number") return null;
  return record.ties
    ? `${record.wins}-${record.losses}-${record.ties}`
    : `${record.wins}-${record.losses}`;
}

/** A trimmed venue, or null. A blank string is not a venue, and a missing one
    renders NOTHING — never "Unknown", never a bare dash standing in for a
    stadium nobody named. */
export function venueOf(fixture: PublicFixture): string | null {
  const venue = fixture.venue?.trim();
  return venue ? venue : null;
}

/**
 * One printable segment of the meta line.
 *
 * A form segment keeps `record` SEPARATE from the club name rather than handing
 * back one joined string, and that is a rendering requirement, not tidiness:
 * the line wraps, and the browser will happily break inside "11-6" at the
 * hyphen. Measured at 375px before this split, three of the sixteen published
 * NFL rows wrapped as "Philadelphia Eagles 11-" / "6" — a record shown as two
 * numbers on two lines, which is a wrong number on the page. The caller wraps
 * `record` in a `nowrap` span; it cannot do that if it is handed a sentence.
 */
export type RowMetaPart =
  | { kind: "venue"; venue: string }
  | { kind: "form"; club: string; record: string; season: number | null };

/**
 * Each club's form — empty when this sport's row carries none (football,
 * today).
 *
 * The season is stated because the record is NOT a recent-form window: it is
 * the last COMPLETED season, which by week 1 is nine months old. "4-13" with
 * no year beside it would read as this season's record, which on the opening
 * weekend would be a fabrication. When both clubs' records come from the same
 * season the year is carried on the last segment only, rather than twice.
 */
export function formOf(fixture: PublicFixture): RowMetaPart[] {
  const lastFive = fixture.lastFive;
  /* The array shape is football's meeting history — deliberately not form. */
  if (Array.isArray(lastFive)) return [];
  /* NBA player form is about players, not a club's record — nothing to put here. */
  if (isPlayerForm(lastFive)) return [];

  const clubs: Array<[string, FixtureSeasonRecord]> = [
    [fixture.home, lastFive.home],
    [fixture.away, lastFive.away],
  ];
  const found = clubs.flatMap(([club, record]) => {
    const line = seasonRecordLine(record);
    const season = typeof record.season === "number" ? record.season : null;
    return line ? [{ club, record: line, season }] : [];
  });
  if (found.length === 0) return [];

  const shared = found.every((f) => f.season !== null && f.season === found[0].season)
    ? found[0].season
    : null;

  return found.map((f, i) => ({
    kind: "form" as const,
    club: f.club,
    record: f.record,
    season: shared !== null ? (i === found.length - 1 ? shared : null) : f.season,
  }));
}

/**
 * The whole meta line: where it is played, and nothing else. An empty array
 * means the row has nothing extra to say and renders no second line at all —
 * which is every football row today.
 *
 * ⚠️ NO SEASON RECORDS ON THE CARD (removed 2026-10-05, Ned). The records were
 * last COMPLETED season's — "New Orleans Saints 6-11 · Atlanta Falcons 8-9
 * (2025)" in October 2026 — so a row on the list carried a number that says
 * nothing about this game, and it pushed the line onto three. The records stay
 * on the fixture page (TeamNewsSection), under a heading that says which season
 * they are. `formOf` remains for that reason and is not called here.
 */
export function rowMeta(fixture: PublicFixture): RowMetaPart[] {
  const venue = venueOf(fixture);
  return venue ? [{ kind: "venue" as const, venue }] : [];
}
